import { useCallback, useMemo } from "react";

import { useTranslation } from "@/translations";
import { Bar, BarChart, DefaultZIndexes, Rectangle, XAxis, YAxis, ZIndexLayer } from "@/ui-adapters/Recharts";
import { RuntimeSvgX } from "@/ui-adapters/RuntimeChartGeometry";

import type { ChartBaseProps } from "./types";

type OpinionatedProgressChartProps = ChartBaseProps & {
  benchmark: number;
};

// Benchmark is positioned at 70% of chart width
const BENCHMARK_POSITION = 0.7;
const WIDTH_INDICATOR = 4;

export function OpinionatedProgressChart({
  value,
  min = 0,
  max = 100,
  renderValue,
  benchmark,
}: OpinionatedProgressChartProps): React.ReactNode {
  const { t } = useTranslation();

  // Convert a data value to its visual position (0 to 1 scale)
  // Values <= benchmark map to 0-70% of chart, values > benchmark map to 70-100%
  const getPositionInPercentage = useCallback(
    (value: number) => {
      if (benchmark === 0) {
        return 0;
      }

      if (value === benchmark && benchmark === max) {
        return 1;
      }

      if (value <= benchmark) {
        return (value / benchmark) * BENCHMARK_POSITION;
      }

      if (benchmark === max) {
        return 1;
      }

      return BENCHMARK_POSITION + ((value - benchmark) / (max - benchmark)) * (1 - BENCHMARK_POSITION);
    },
    [benchmark, max],
  );

  const chartData = useMemo(() => {
    // Define segment boundaries in data units
    const boundaries = [min, benchmark * 0.1, benchmark * 0.3, benchmark * 0.9, max];
    const colors = [
      "var(--color-background-danger)",
      "var(--color-background-warning)",
      "var(--color-chart-threshold-caution)",
      "var(--color-background-success)",
    ];
    const names = ["red", "orange", "yellow", "green"];

    // Calculate visual positions for boundaries
    const listPositionInPercentage = boundaries.map((currValue) => getPositionInPercentage(currValue));

    // Create segments with visual widths (scaled to max for recharts domain)
    return names.map((name, index) => ({
      name,
      value: (listPositionInPercentage[index + 1] - listPositionInPercentage[index]) * max,
      color: colors[index],
      fill: colors[index],
    }));
  }, [benchmark, max, min, getPositionInPercentage]);

  // Calculate indicator position using relative scaling, clamped so out-of-range values stay on the bar
  const posIndicatorPercentage = Math.min(Math.max(getPositionInPercentage(value), 0), 1);
  const isDataAvailable = benchmark !== 0;

  return (
    <div className="w-full">
      <BarChart
        layout="vertical"
        margin={{ top: 0, right: 0, left: 0, bottom: -40 }}
        data={[{ name: "primary", value }]}
        className="w-full"
        height={56}
        responsive
      >
        <XAxis type="number" domain={[min, max]} hide />
        <YAxis type="category" dataKey="name" hide />

        {isDataAvailable ? (
          <>
            {/* Background colored segments */}
            {chartData.map((segment, index) => {
              let radius: [number, number, number, number] = [0, 0, 0, 0];
              if (index === 0) {
                radius = [2, 0, 0, 2];
              } else if (index === chartData.length - 1) {
                radius = [0, 2, 2, 0];
              }

              return (
                <Bar
                  stackId="background"
                  key={segment.name}
                  dataKey={() => segment.value}
                  radius={radius}
                  fill={segment.color}
                  barSize={8}
                  isAnimationActive={false}
                  shape={(props) => <Rectangle {...props} />}
                />
              );
            })}
          </>
        ) : (
          <Bar
            stackId="background"
            dataKey={() => max}
            radius={[2, 2, 2, 2]}
            fill="var(--color-border-default)"
            barSize={8}
            isAnimationActive={false}
            shape={(props) => <Rectangle {...props} />}
          />
        )}
        <ZIndexLayer zIndex={DefaultZIndexes.bar + 1}>
          <text x={0} y={36} className="fill-text-brand-secondary text-figure-lg" textAnchor="start">
            {!isDataAvailable && t("common.label.not-applicable")}
            {isDataAvailable && !!renderValue && renderValue(value)}
            {isDataAvailable && !renderValue && value}
          </text>
          {isDataAvailable && (
            <RuntimeSvgX
              x={`max(0px, calc(${posIndicatorPercentage * 100}% - ${posIndicatorPercentage === 0 ? 0 : WIDTH_INDICATOR}px))`}
            >
              {(positionAttributes) => (
                <rect
                  {...positionAttributes}
                  className="fill-background-brand-secondary"
                  width={WIDTH_INDICATOR}
                  height={16}
                  y={40}
                  rx={2}
                />
              )}
            </RuntimeSvgX>
          )}
        </ZIndexLayer>
      </BarChart>
    </div>
  );
}
